import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef, useEffect, useState } from "react";
import Marquee from "react-fast-marquee";
import {
  CalendarCheck, Activity, Search, FileText, ArrowRight,
  ShieldCheck, Clock, Sparkles, MapPin,
} from "lucide-react";
import { Reveal, MaskLines } from "../components/Motion";
import { BRAND } from "../lib/appConfig";
import { getLiveQueue } from "../lib/api";

const FEATURES = [
  { icon: MapPin, title: "Find care near you", body: "Choose your state, city and the exact clinic or doctor — search across both in seconds." },
  { icon: CalendarCheck, title: "Effortless booking", body: "A guided five-step flow with instant confirmation and your queue token." },
  { icon: Activity, title: "Live queue, real time", body: "Watch your position update live. Arrive exactly when it's your turn." },
  { icon: FileText, title: "Digital prescriptions", body: "Medicines and advice, organised and always within reach." },
];

const CHAPTERS = [
  { n: "01", title: "Never guess your wait again", body: "A live, honest queue that respects your time — down to the token." },
  { n: "02", title: "Booking that feels human", body: "Plain language, gentle steps and instant confirmation." },
  { n: "03", title: "Everything, in one calm place", body: "Location, provider, queue and prescriptions — effortless to reach." },
];

export default function Landing() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  const [queue, setQueue] = useState(null);

  useEffect(() => {
    let mounted = true;
    async function load() {
      try {
        const data = await getLiveQueue();
        if (!mounted) return;
        setQueue(data || null);
      } catch (e) {
        // ignore
      }
    }
    load();
    const id = setInterval(load, 10000);
    return () => { mounted = false; clearInterval(id); };
  }, []);

  return (
    <div data-testid="landing-page" className="overflow-hidden">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-[92vh] flex items-center pt-28 pb-20">
        <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0 -z-10">
          <img src={BRAND.logo} alt="" className="h-full w-full object-cover opacity-15 dark:opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-background/70 via-background/85 to-background" />
        </motion.div>
        <div className="glow-teal pointer-events-none absolute top-1/3 -left-40 h-[520px] w-[520px] blur-[120px] opacity-70" />

        <div className="relative mx-auto max-w-7xl px-5 sm:px-8 w-full">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-border glass-soft px-4 py-1.5 text-xs font-semibold tracking-wide text-foreground/90">
            <Sparkles className="h-3.5 w-3.5 text-primary" /> Connected care. Redefined.
          </motion.div>

          <h1 className="mt-6 font-display font-medium text-foreground text-5xl sm:text-6xl lg:text-7xl leading-[0.98] tracking-tight max-w-4xl">
            <MaskLines lines={["Book care in", "seconds — skip"]} start={0.15} />
            <span className="line-mask block">
              <motion.span className="block aurora-text" initial={{ y: "110%" }} animate={{ y: "0%" }}
                transition={{ duration: 1.05, delay: 0.15 + 2 * 0.12, ease: [0.16, 1, 0.3, 1] }}>
                the waiting room.
              </motion.span>
            </span>
          </h1>

          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.7 }}
            className="mt-7 max-w-xl text-lg text-muted-foreground leading-relaxed">
            Find a clinic or doctor anywhere in India, book an appointment in five calm steps,
            and track your live queue position in real time.
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.85 }}
            className="mt-9 flex flex-col sm:flex-row gap-3">
            <Link to="/book" data-testid="hero-book-btn"
              className="group aurora-gradient text-white font-semibold px-7 py-3.5 rounded-full inline-flex items-center justify-center gap-2 hover:scale-[1.02] transition-transform aurora-ring btn-shine">
              Book an appointment <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/queue" data-testid="hero-queue-btn"
              className="glass-soft text-foreground font-semibold px-7 py-3.5 rounded-full inline-flex items-center justify-center gap-2 hover:bg-foreground/5 transition-colors">
              <Activity className="h-4 w-4 text-primary" /> Track live queue
            </Link>
          </motion.div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 1 }}
            className="mt-10 flex items-center gap-6 text-sm text-muted-foreground">
            <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-primary" /> Private & secure</span>
            <span className="flex items-center gap-2"><Clock className="h-4 w-4 text-primary" /> Real-time updates</span>
          </motion.div>

          {/* floating live card */}
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 1.1 }}
            className="glass rounded-3xl p-5 w-64 mt-14 lg:absolute lg:right-8 lg:bottom-8 lg:mt-0">
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Now serving</p>
            <div className="mt-1 flex items-end gap-2">
              <span className="font-display text-4xl font-medium aurora-text">{queue?.nowServing ? `#${queue.nowServing}` : "—"}</span>
              <span className="relative mb-2 inline-flex text-primary"><span className="pulse-ring h-2.5 w-2.5 rounded-full bg-primary relative" /></span>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">You&apos;re token <b className="text-primary">{queue?.yourToken ? `#${queue.yourToken}` : "—"}</b> · ~{queue?.averageMinutesPerPatient ?? "—"} min</p>
          </motion.div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="border-y border-border bg-muted/30 py-5">
        <Marquee speed={40} gradient={false} autoFill>
          {["Premium Care", "Expert Physicians", "Real-time Queue", "Zero Paperwork", "Trusted by Clinics", "Digital Prescriptions"].map((t, i) => (
            <span key={i} className="mx-8 inline-flex items-center gap-8 font-display text-2xl text-muted-foreground/50">
              {t} <span className="h-1.5 w-1.5 rounded-full aurora-gradient" />
            </span>
          ))}
        </Marquee>
      </div>

      {/* FEATURES */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-24">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-primary">What you get</p>
          <h2 className="mt-3 font-display text-4xl sm:text-5xl font-medium text-foreground max-w-2xl leading-tight">Every step of care, thoughtfully simplified.</h2>
        </Reveal>
        <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {FEATURES.map((f, i) => (
            <Reveal key={f.title} delay={i * 0.08}>
              <div className="group h-full glass gradient-border card-hover rounded-3xl p-8">
                <div className="h-12 w-12 rounded-2xl aurora-gradient grid place-items-center text-white"><f.icon className="h-6 w-6" /></div>
                <h3 className="mt-6 font-display text-2xl text-foreground font-medium">{f.title}</h3>
                <p className="mt-3 text-muted-foreground leading-relaxed">{f.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="py-24 border-y border-border bg-card/20">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-primary font-semibold">The manifesto</p>
            <h2 className="mt-4 font-display text-4xl sm:text-6xl font-medium max-w-3xl leading-[1.05] text-foreground">Care should feel calm, clear and completely yours.</h2>
          </Reveal>
          <div className="mt-16 divide-y divide-border border-t border-border">
            {CHAPTERS.map((c, i) => (
              <Reveal key={c.n} delay={i * 0.06}>
                <div className="grid md:grid-cols-12 gap-6 py-10 group">
                  <div className="md:col-span-2 font-mono-cq text-primary text-lg">{c.n}</div>
                  <h3 className="md:col-span-6 font-display text-3xl sm:text-4xl text-foreground group-hover:translate-x-2 transition-transform duration-500">{c.title}</h3>
                  <p className="md:col-span-4 text-muted-foreground leading-relaxed self-center">{c.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-24">
        <Reveal>
          <div className="relative overflow-hidden rounded-[2.5rem] glass px-8 sm:px-16 py-16 sm:py-20">
            <div className="glow-teal absolute -right-20 -bottom-20 h-80 w-80 blur-[100px]" />
            <div className="relative z-10 max-w-2xl">
              <h2 className="font-display text-4xl sm:text-5xl font-medium leading-tight text-foreground">Ready when you are.</h2>
              <p className="mt-4 text-muted-foreground text-lg">Find your clinic or doctor and book in under a minute — no waiting-room guesswork.</p>
              <Link to="/book" data-testid="cta-book-btn"
                className="mt-8 inline-flex items-center gap-2 aurora-gradient text-white font-semibold px-7 py-3.5 rounded-full hover:scale-[1.02] transition-transform aurora-ring">
                Get started <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
